import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
} from "framer-motion";
import { createPageUrl } from "../../utils";
import { Button } from "../../components/ui/button";
import {
  Briefcase,
  Users,
  FileText,
  UserCog,
  Building2,
  Sparkles,
  Globe,
  Zap,
  Shield,
  ArrowRight,
  ArrowUpRight,
  Brain,
  Layers,
  Rocket,
  BarChart3,
  XCircle,
  Clock,
  AlertCircle,
  Bot,
  FileSearch,
  MessageSquare,
  Workflow,
  Play,
  DollarSign,
  Users2,
  Fingerprint,
  CheckCircle2,
  UserSearch,
  ClipboardCheck,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/*  DESIGN TOKENS, Technical Minimalist                               */
/* ------------------------------------------------------------------ */
const C = {
  paper: "#F7F7F5",
  forest: "#1A3C2B",
  forestSoft: "rgba(26,60,43,0.08)",
  grid: "#3A3A38",
  hairline: "rgba(58,58,56,0.20)",
  coral: "#FF8C69",
  mint: "#9EFFBF",
  gold: "#F4D35E",
};

/* ------------------------------------------------------------------ */
/*  CONTENT                                                            */
/* ------------------------------------------------------------------ */

const products = [
  {
    icon: Briefcase,
    title: "WebIdeal Jobs",
    subtitle: "AI Job Portal",
    description:
      "Smart job matching with predictive fit scoring for candidates and employers.",
    accent: "#0044CC",
    href: createPageUrl("ProductJobs"),
  },
  {
    icon: UserSearch,
    title: "WebIdeal Recruit",
    subtitle: "CRM & Recruitment",
    description:
      "Pipeline automation, client management and intelligent candidate sourcing.",
    accent: "#00AAFF",
    href: createPageUrl("ProductRecruit"),
  },
  {
    icon: ClipboardCheck,
    title: "WebIdeal ATS",
    subtitle: "Applicant Tracking",
    description:
      "AI-powered screening, ranking and automated workflow management.",
    accent: "#6366F1",
    href: createPageUrl("ProductATS"),
  },
  {
    icon: UserCog,
    title: "WebIdeal HRMS",
    subtitle: "HR Management",
    description:
      "Complete employee lifecycle management with payroll and compliance.",
    accent: "#0EA5A4",
    href: createPageUrl("ProductHRMS"),
  },

];

const problems = [
  { icon: XCircle, title: "Disconnected Systems", description: "Multiple tools that don't talk to each other." },
  { icon: Clock, title: "Slow Time-to-Hire", description: "Manual processes delay critical hiring decisions." },
  { icon: AlertCircle, title: "Poor Candidate Experience", description: "Outdated communication and tracking methods." },
  { icon: BarChart3, title: "No Predictive Insights", description: "Hiring decisions based on gut feel, not data." },
];

const whyChooseUs = [
  { icon: Layers, title: "True Unified Platform", description: "One ecosystem integrating ATS, CRM, HRMS and Job Portal." },
  { icon: Brain, title: "AI-First Architecture", description: "Predictive analytics at every stage, not bolted on." },
  { icon: Zap, title: "Workflow Automation", description: "Automate hiring, onboarding and HR tasks intelligently." },
  { icon: BarChart3, title: "Predictive Insights", description: "AI fit scoring and performance forecasting." },
  { icon: DollarSign, title: "Affordable Excellence", description: "Enterprise features at SMB-friendly pricing." },
  { icon: Globe, title: "Global by Design", description: "Multi-country compliance and localized workflows." },
  { icon: Shield, title: "Enterprise Security", description: "SOC 2, GDPR-ready, bank-grade encryption." },
  { icon: Fingerprint, title: "White-Label Ready", description: "Custom branding for a seamless client experience." },
  { icon: Users2, title: "Scalable Growth", description: "Scales from startups to global enterprises." },
];

const aiCapabilities = [
  { id: "parse",     icon: FileSearch,    title: "Resume Parsing",         description: "LLM extraction of skills, experience and qualifications.", accent: C.coral, short: "Parse" },
  { id: "score",     icon: Brain,         title: "Predictive Fit Scoring", description: "AI ranks candidates against role and history patterns.",   accent: C.mint,  short: "Score" },
  { id: "jd",        icon: MessageSquare, title: "JD Generation",          description: "Auto-generate SEO-optimised job descriptions.",            accent: C.gold,  short: "Draft" },
  { id: "chat",      icon: Bot,           title: "Candidate Chatbot",      description: "24/7 assistant for queries, scheduling and engagement.",   accent: C.coral, short: "Chat"  },
  { id: "workflow",  icon: Workflow,      title: "Workflow Automation",    description: "Plug into Zapier, Make.com and UiPath.",                   accent: C.mint,  short: "Flow"  },
  { id: "analytics", icon: BarChart3,     title: "Predictive Analytics",   description: "Forecast hiring needs and recruitment ROI.",               accent: C.gold,  short: "Sense" },
];

const stats = [
  { value: "70%", label: "Time saved in screening" },
  { value: "3x", label: "Faster average time-to-hire" },
  { value: "85%", label: "AI match accuracy" },
  { value: "50%", label: "Cost reduction per hire" },
  { value: "24/7", label: "Candidate AI assistant" },
  { value: "5+", label: "Countries served" },
];

const countries = [
  { country: "India", code: "IN" },
  { country: "United States", code: "US" },
  { country: "United Kingdom", code: "UK" },
  { country: "Canada", code: "CA" },
  { country: "Germany", code: "DE" },
];

/* ------------------------------------------------------------------ */
/*  REUSABLE COMPONENTS                                                */
/* ------------------------------------------------------------------ */

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.05, ease: [0.25, 0.1, 0.25, 1] },
  }),
};

/** Inline tech label: 10px uppercase tracked, mimics monospace feel without changing font. */
function TechLabel({ children, className = "", as: As = "span" }) {
  return (
    <As
      className={`text-[10px] font-bold uppercase ${className}`}
      style={{ letterSpacing: "0.18em", fontVariantNumeric: "tabular-nums" }}
    >
      {children}
    </As>
  );
}

/** Status Badge, square dot + uppercase tracked label inside a 1px hairline. */
function StatusBadge({ children, color = C.forest, className = "" }) {
  return (
    <span
      className={`inline-flex items-center gap-2 border px-3 py-1 ${className}`}
      style={{ borderColor: `${color}33`, color }}
    >
      <span className="w-2 h-2" style={{ background: color }} aria-hidden="true" />
      <TechLabel>{children}</TechLabel>
    </span>
  );
}

/** L-shaped corner markers in each corner of a relative parent. */
function CornerMarkers({ color = C.forest, size = 10 }) {
  const s = `${size}px`;
  const style = (pos) => ({
    position: "absolute",
    width: s,
    height: s,
    [pos.v]: 0,
    [pos.h]: 0,
    borderColor: color,
  });
  return (
    <>
      <span aria-hidden="true" className="border-l-2 border-t-2" style={style({ v: "top", h: "left" })} />
      <span aria-hidden="true" className="border-r-2 border-t-2" style={style({ v: "top", h: "right" })} />
      <span aria-hidden="true" className="border-l-2 border-b-2" style={style({ v: "bottom", h: "left" })} />
      <span aria-hidden="true" className="border-r-2 border-b-2" style={style({ v: "bottom", h: "right" })} />
    </>
  );
}

/** Page-wide mosaic background, fixed, behind everything, very subtle. */
function MosaicBackground() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 -z-10"
      style={{ background: C.paper }}
    >
      <svg
        width="100%"
        height="100%"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <pattern id="mosaic" width="240" height="240" patternUnits="userSpaceOnUse">
            <rect width="240" height="240" fill={C.paper} />
            <g
              fill="none"
              stroke={C.grid}
              strokeOpacity="0.18"
              strokeWidth="0.5"
              shapeRendering="crispEdges"
            >
              <rect x="0" y="0" width="120" height="120" />
              <rect x="120" y="0" width="120" height="80" />
              <rect x="120" y="80" width="80" height="80" />
              <rect x="200" y="80" width="40" height="160" />
              <rect x="0" y="120" width="80" height="120" />
              <rect x="80" y="120" width="120" height="60" />
              <rect x="80" y="180" width="120" height="60" />
            </g>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#mosaic)" />
      </svg>
    </div>
  );
}

/** Hero blueprint topology, square container, dashed orbit, central + 4 product nodes. */
function BlueprintTopology() {
  const reduce = useReducedMotion();
  const nodes = [
    { id: "ats", label: "ATS", icon: FileText, angle: -90 },
    { id: "crm", label: "CRM", icon: Users, angle: 0 },
    { id: "hrms", label: "HRMS", icon: Building2, angle: 90 },
    { id: "jobs", label: "JOBS", icon: Briefcase, angle: 180 },
  ];
  const radius = 140;
  const center = { x: 200, y: 200 };

  return (
    <div className="relative w-full max-w-[520px] mx-auto">
      <div
        className="relative aspect-square border"
        style={{ borderColor: C.hairline }}
      >
        <CornerMarkers color={C.forest} />

        {/* Tech metadata */}
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className="w-2 h-2" style={{ background: C.forest }} />
          <TechLabel style={{ color: C.forest }}>Topology · v2.0</TechLabel>
        </div>
        <TechLabel
          className="absolute top-3 right-3"
          style={{ color: `${C.grid}99` }}
        >
          04 NODES / 1 CORE
        </TechLabel>

        <svg viewBox="0 0 400 400" className="w-full h-full" aria-hidden="true">
          {/* Connection lines (1px @ 0.2 opacity) */}
          {nodes.map((n) => {
            const rad = (n.angle * Math.PI) / 180;
            const x = center.x + Math.cos(rad) * radius;
            const y = center.y + Math.sin(rad) * radius;
            return (
              <line
                key={`l-${n.id}`}
                x1={center.x}
                y1={center.y}
                x2={x}
                y2={y}
                stroke={C.forest}
                strokeOpacity="0.2"
                strokeWidth="1"
                shapeRendering="crispEdges"
              />
            );
          })}

          {/* Dashed circular orbit */}
          <circle
            cx={center.x}
            cy={center.y}
            r={radius}
            fill="none"
            stroke={C.forest}
            strokeOpacity="0.35"
            strokeWidth="1"
            strokeDasharray="4 6"
          />

          {/* Cardinal axis hairlines */}
          <line x1="0" y1="200" x2="400" y2="200" stroke={C.grid} strokeOpacity="0.1" strokeWidth="0.5" />
          <line x1="200" y1="0" x2="200" y2="400" stroke={C.grid} strokeOpacity="0.1" strokeWidth="0.5" />

          {/* Central solid forest node */}
          <rect x="192" y="192" width="16" height="16" fill={C.forest} />
        </svg>

        {/* Orbiting wrapper rotates the whole node group linearly */}
        <motion.div
          className="absolute inset-0"
          animate={reduce ? {} : { rotate: 360 }}
          transition={{ duration: 28, ease: "linear", repeat: Infinity }}
          style={{ transformOrigin: "50% 50%" }}
        >
          {nodes.map((n) => {
            const rad = (n.angle * Math.PI) / 180;
            const xPct = 50 + (Math.cos(rad) * radius * 100) / 400;
            const yPct = 50 + (Math.sin(rad) * radius * 100) / 400;
            return (
              <motion.div
                key={n.id}
                className="absolute"
                style={{
                  left: `${xPct}%`,
                  top: `${yPct}%`,
                  transform: "translate(-50%, -50%)",
                }}
                /* counter-rotate so node icons stay upright */
                animate={reduce ? {} : { rotate: -360 }}
                transition={{ duration: 28, ease: "linear", repeat: Infinity }}
              >
                <div
                  className="w-12 h-12 flex items-center justify-center bg-[#F7F7F5] border"
                  style={{ borderColor: C.forest, color: C.forest }}
                >
                  <n.icon className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <TechLabel
                  className="block text-center mt-1.5"
                  style={{ color: C.forest }}
                >
                  {n.label}
                </TechLabel>
              </motion.div>
            );
          })}
        </motion.div>
      </div>

      {/* Footer metadata */}
      <div
        className="mt-3 flex items-center justify-between border-t pt-2"
        style={{ borderColor: C.hairline }}
      >
        <TechLabel style={{ color: `${C.grid}99` }}>
          platform topology
        </TechLabel>
        <TechLabel style={{ color: C.forest }}>
          status · operational
        </TechLabel>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  AI ORCHESTRATOR, animated pipeline showing a candidate packet     */
/*  flowing through all 6 agents in real time.                         */
/* ------------------------------------------------------------------ */
function AIOrchestrator({ agents }) {
  const reduce = useReducedMotion();
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (reduce) return undefined;
    const id = setInterval(
      () => setActiveIdx((prev) => (prev + 1) % agents.length),
      1500
    );
    return () => clearInterval(id);
  }, [agents.length, reduce]);

  const pct = Math.round(((activeIdx + 1) / agents.length) * 100);

  return (
    <div
      className="relative border mb-12"
      style={{ borderColor: C.hairline, background: "#FFFFFF" }}
    >
      {/* Header strip */}
      <div
        className="flex items-center justify-between px-5 md:px-6 py-3 border-b flex-wrap gap-2"
        style={{ borderColor: C.hairline }}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="w-2 h-2 animate-pulse"
            style={{ background: C.forest }}
            aria-hidden="true"
          />
          <TechLabel style={{ color: C.forest }}>Live Orchestrator</TechLabel>
          <span className="hidden sm:inline-block w-px h-3 mx-1" style={{ background: C.hairline }} />
          <TechLabel style={{ color: `${C.grid}aa` }}>
            stage {String(activeIdx + 1).padStart(2, "0")} / 06
          </TechLabel>
        </div>
        <TechLabel style={{ color: `${C.grid}99` }}>
          packet · cnd-{(0x2470 + activeIdx).toString(16).toUpperCase()}
        </TechLabel>
      </div>

      {/* Pipeline */}
      <div className="px-3 sm:px-5 md:px-8 py-8 relative overflow-x-auto">
        <div className="relative grid grid-cols-6 min-w-[640px] gap-2">
          {agents.map((a, i) => {
            const isActive = i === activeIdx;
            const isPast = i < activeIdx;
            const lineLit = isActive || isPast;
            return (
              <div
                key={a.id}
                className="relative flex flex-col items-center"
              >
                {/* Connector line to the next node */}
                {i < agents.length - 1 && (
                  <div
                    className="absolute top-7 h-px transition-colors duration-500 z-0"
                    style={{
                      left: "60%",
                      width: "80%",
                      background: lineLit ? a.accent : C.hairline,
                    }}
                  >
                    {/* Travelling packet */}
                    <motion.span
                      className="absolute -top-[3px] w-2 h-2"
                      style={{
                        background: a.accent,
                        boxShadow: isActive ? `0 0 0 3px ${a.accent}33` : "none",
                      }}
                      initial={false}
                      animate={
                        isActive && !reduce
                          ? { left: ["0%", "100%"], opacity: [1, 1, 0] }
                          : { left: "0%", opacity: 0 }
                      }
                      transition={{ duration: 1.4, ease: "linear" }}
                    />
                  </div>
                )}

                {/* Node */}
                <motion.div
                  className="relative w-14 h-14 flex items-center justify-center border-2 z-10 transition-colors duration-300"
                  animate={{
                    background: isActive ? a.accent : "#FFFFFF",
                    borderColor: isActive || isPast ? a.accent : C.hairline,
                    scale: isActive ? 1.08 : 1,
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <a.icon
                    className="w-5 h-5 transition-colors duration-300"
                    strokeWidth={1.6}
                    style={{
                      color: isActive
                        ? C.forest
                        : isPast
                        ? a.accent
                        : `${C.grid}99`,
                    }}
                  />
                  {/* Pulse ring on active node */}
                  {isActive && !reduce && (
                    <motion.span
                      className="absolute inset-0 border-2 pointer-events-none"
                      style={{ borderColor: a.accent }}
                      initial={{ scale: 1, opacity: 1 }}
                      animate={{ scale: 1.7, opacity: 0 }}
                      transition={{ duration: 1.4, ease: "easeOut" }}
                    />
                  )}
                </motion.div>

                {/* Label */}
                <div className="mt-3 text-center">
                  <TechLabel
                    style={{
                      color: isActive
                        ? C.forest
                        : isPast
                        ? a.accent
                        : `${C.grid}99`,
                    }}
                  >
                    0{i + 1} · {a.short}
                  </TechLabel>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer progress bar */}
      <div className="border-t" style={{ borderColor: C.hairline }}>
        <div className="px-5 md:px-6 py-3 flex items-center gap-4">
          <div
            className="flex-1 h-1 relative overflow-hidden"
            style={{ background: C.hairline }}
          >
            <motion.div
              className="absolute inset-y-0 left-0"
              style={{ background: C.forest }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            />
          </div>
          <TechLabel
            className="tabular-nums"
            style={{ color: C.forest, minWidth: "32px", textAlign: "right" }}
          >
            {pct}%
          </TechLabel>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  AGENT CARD WITH EMBEDDED MINI DEMO                                 */
/* ------------------------------------------------------------------ */
function AgentCard({ agent, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.45, delay: index * 0.05 }}
      className="relative p-7 md:p-8 group cursor-pointer transition-colors duration-150 hover:bg-white"
      style={{ background: C.paper, borderLeft: `4px solid ${agent.accent}` }}
    >
      <div className="flex items-center justify-between mb-5">
        <div
          className="w-11 h-11 flex items-center justify-center"
          style={{ background: C.forest, color: C.paper }}
        >
          <agent.icon className="w-5 h-5" strokeWidth={1.6} />
        </div>
        <TechLabel style={{ color: `${C.grid}99` }}>
          Agent · 0{index + 1}
        </TechLabel>
      </div>
      <h3 className="text-xl font-bold leading-tight" style={{ color: C.forest }}>
        {agent.title}
      </h3>
      <p
        className="mt-2 text-sm leading-relaxed mb-5"
        style={{ color: C.grid }}
      >
        {agent.description}
      </p>

      {/* Live mini-demo for this agent */}
      <AgentMiniDemo id={agent.id} accent={agent.accent} />
    </motion.div>
  );
}

function DemoFrame({ children, label }) {
  return (
    <div
      className="relative border bg-white p-3"
      style={{ borderColor: C.hairline }}
    >
      <div className="absolute top-1.5 right-2 flex items-center gap-1">
        <span
          className="w-1.5 h-1.5 rounded-full animate-pulse"
          style={{ background: C.forest }}
          aria-hidden="true"
        />
        <span
          className="text-[8px] font-bold uppercase"
          style={{ letterSpacing: "0.2em", color: `${C.grid}88` }}
        >
          {label}
        </span>
      </div>
      <div className="pt-2">{children}</div>
    </div>
  );
}

function AgentMiniDemo({ id, accent }) {
  const reduce = useReducedMotion();
  switch (id) {
    case "parse":     return <DemoParse accent={accent} reduce={reduce} />;
    case "score":     return <DemoScore accent={accent} reduce={reduce} />;
    case "jd":        return <DemoJD accent={accent} reduce={reduce} />;
    case "chat":      return <DemoChat accent={accent} reduce={reduce} />;
    case "workflow":  return <DemoWorkflow accent={accent} reduce={reduce} />;
    case "analytics": return <DemoAnalytics accent={accent} reduce={reduce} />;
    default:          return null;
  }
}

/*, 01 · Resume parser: scanning lines + skill chips popping out, */
function DemoParse({ accent, reduce }) {
  const widths = [82, 96, 70];
  const skills = ["React", "AWS", "SQL"];
  return (
    <DemoFrame label="Live">
      <div className="flex items-center gap-3">
        <div className="flex-1 space-y-1.5">
          {widths.map((w, i) => (
            <div
              key={i}
              className="h-1.5 relative overflow-hidden"
              style={{ background: C.hairline, width: `${w}%` }}
            >
              <motion.div
                className="absolute inset-y-0"
                style={{ background: accent, width: "40%", left: 0 }}
                animate={
                  reduce ? { left: "60%" } : { left: ["-40%", "100%"] }
                }
                transition={{
                  duration: 2.4,
                  delay: i * 0.35,
                  ease: "linear",
                  repeat: Infinity,
                }}
              />
            </div>
          ))}
        </div>
        <div className="flex flex-col items-end gap-1 w-16">
          {skills.map((s, i) => (
            <motion.span
              key={s}
              className="px-1.5 py-0.5 text-[9px] font-bold whitespace-nowrap"
              style={{ background: accent, color: C.forest }}
              initial={{ opacity: 0, x: 10 }}
              animate={
                reduce
                  ? { opacity: 1, x: 0 }
                  : { opacity: [0, 1, 1, 0], x: [10, 0, 0, -10] }
              }
              transition={{
                duration: 4,
                delay: i * 0.6 + 0.4,
                repeat: Infinity,
                ease: "easeOut",
              }}
            >
              {s}
            </motion.span>
          ))}
        </div>
      </div>
    </DemoFrame>
  );
}

/*, 02 · Predictive fit score: gauge fills with live counter, */
function DemoScore({ accent, reduce }) {
  return (
    <DemoFrame label="Match">
      <div className="flex items-center justify-between mb-1.5">
        <TechLabel style={{ color: C.grid }}>fit · score</TechLabel>
        <span
          className="text-xs font-extrabold tabular-nums"
          style={{ color: C.forest }}
        >
          <Counter from={0} to={92} duration={1.6} reduce={reduce} />%
        </span>
      </div>
      <div
        className="h-2 relative overflow-hidden"
        style={{ background: C.hairline }}
      >
        <motion.div
          className="absolute inset-y-0 left-0"
          style={{ background: accent }}
          initial={{ width: "0%" }}
          animate={
            reduce
              ? { width: "92%" }
              : { width: ["0%", "92%", "92%", "0%"] }
          }
          transition={{
            duration: 4,
            ease: "easeInOut",
            times: [0, 0.4, 0.85, 1],
            repeat: Infinity,
          }}
        />
      </div>
      <div
        className="mt-2 flex items-center gap-1.5 text-[9px] font-bold uppercase"
        style={{ letterSpacing: "0.18em", color: `${C.grid}99` }}
      >
        <span
          className="w-1 h-1 rounded-full"
          style={{ background: accent }}
        />
        ranked above 87% of pool
      </div>
    </DemoFrame>
  );
}

function Counter({ from, to, duration, reduce }) {
  const [v, setV] = useState(reduce ? to : from);
  useEffect(() => {
    if (reduce) {
      setV(to);
      return undefined;
    }
    let raf;
    let start;
    const period = 4; // seconds, matches DemoScore loop
    const tick = (ts) => {
      if (start === undefined) start = ts;
      const t = ((ts - start) / 1000) % period;
      let next;
      if (t < duration) next = from + (to - from) * (t / duration);
      else if (t < period * 0.85) next = to;
      else next = to - (to - from) * ((t - period * 0.85) / (period * 0.15));
      setV(Math.max(from, Math.min(to, Math.round(next))));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [from, to, duration, reduce]);
  return <>{v}</>;
}

/*, 03 · JD generation: code-block typewriter, */
function DemoJD({ accent, reduce }) {
  const lines = [
    "// senior-engineer.jd",
    "title: 'Senior Engineer',",
    "skills: ['React', 'AWS'],",
  ];
  return (
    <DemoFrame label="Draft">
      <div
        className="font-mono text-[10px] leading-[1.55] space-y-0.5"
        style={{ color: C.forest }}
      >
        {lines.map((line, i) => (
          <Typewriter
            key={i}
            text={line}
            delay={i * 1}
            accent={accent}
            reduce={reduce}
            isLast={i === lines.length - 1}
          />
        ))}
      </div>
    </DemoFrame>
  );
}

function Typewriter({ text, delay, accent, reduce, isLast }) {
  const [count, setCount] = useState(reduce ? text.length : 0);
  useEffect(() => {
    if (reduce) {
      setCount(text.length);
      return undefined;
    }
    let raf;
    let start;
    const total = 5; // global cycle
    const tick = (ts) => {
      if (start === undefined) start = ts;
      const t = (((ts - start) / 1000) + delay) % total;
      let n;
      if (t < 1) n = Math.floor((t / 1) * text.length);
      else if (t < 4) n = text.length;
      else n = Math.max(0, text.length - Math.floor(((t - 4) / 1) * text.length));
      setCount(Math.max(0, Math.min(text.length, n)));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [text, delay, reduce]);

  return (
    <div className="whitespace-nowrap overflow-hidden">
      <span>{text.slice(0, count)}</span>
      {isLast && (
        <motion.span
          className="inline-block w-[5px] h-[10px] ml-0.5 align-middle"
          style={{ background: accent }}
          animate={reduce ? {} : { opacity: [1, 0, 1] }}
          transition={{ duration: 0.8, repeat: Infinity }}
        />
      )}
    </div>
  );
}

/*, 04 · Chatbot bubbles, */
function DemoChat({ accent, reduce }) {
  return (
    <DemoFrame label="Online">
      <div className="space-y-1.5 min-h-[44px]">
        <motion.div
          className="text-[10px] px-2 py-1 inline-block max-w-[80%]"
          style={{ background: C.hairline, color: C.forest }}
          initial={{ opacity: 0, x: -8 }}
          animate={
            reduce
              ? { opacity: 1, x: 0 }
              : { opacity: [0, 1, 1, 1, 0], x: [-8, 0, 0, 0, -8] }
          }
          transition={{ duration: 4, repeat: Infinity }}
        >
          When can I interview?
        </motion.div>
        <motion.div
          className="block text-[10px] px-2 py-1 max-w-[80%] ml-auto text-right font-medium"
          style={{ background: accent, color: C.forest, width: "fit-content" }}
          initial={{ opacity: 0, x: 8 }}
          animate={
            reduce
              ? { opacity: 1, x: 0 }
              : { opacity: [0, 0, 1, 1, 0], x: [8, 8, 0, 0, 8] }
          }
          transition={{ duration: 4, repeat: Infinity }}
        >
          Tomorrow · 3pm ✓
        </motion.div>
      </div>
    </DemoFrame>
  );
}

/*, 05 · Workflow: travelling node along connected boxes, */
function DemoWorkflow({ accent, reduce }) {
  const xs = [22, 100, 178];
  return (
    <DemoFrame label="Flow">
      <svg viewBox="0 0 200 32" className="w-full h-9" aria-hidden="true">
        {xs.map((cx, i) => (
          <g key={i}>
            {i < xs.length - 1 && (
              <line
                x1={cx + 7}
                y1={16}
                x2={xs[i + 1] - 7}
                y2={16}
                stroke={accent}
                strokeOpacity="0.6"
                strokeWidth="1"
                strokeDasharray="2 2"
              />
            )}
            <rect
              x={cx - 7}
              y={9}
              width="14"
              height="14"
              fill="none"
              stroke={accent}
              strokeWidth="1.4"
            />
            <rect
              x={cx - 3}
              y={13}
              width="6"
              height="6"
              fill={accent}
              fillOpacity="0.4"
            />
          </g>
        ))}
        {!reduce && (
          <motion.circle
            r="3.5"
            fill={accent}
            cy={16}
            initial={{ cx: xs[0] }}
            animate={{ cx: [xs[0], xs[1], xs[2], xs[0]] }}
            transition={{
              duration: 3,
              ease: "linear",
              repeat: Infinity,
              times: [0, 0.4, 0.8, 1],
            }}
          />
        )}
      </svg>
    </DemoFrame>
  );
}

/*, 06 · Analytics: animated bars, */
function DemoAnalytics({ accent, reduce }) {
  const peaks = [55, 88, 62, 95, 70, 80];
  return (
    <DemoFrame label="Live">
      <div className="flex items-end gap-1.5 h-12">
        {peaks.map((h, i) => (
          <motion.div
            key={i}
            className="flex-1 origin-bottom"
            style={{ background: accent }}
            initial={{ height: `${h * 0.5}%` }}
            animate={
              reduce
                ? { height: `${h}%` }
                : {
                    height: [
                      `${h * 0.55}%`,
                      `${h}%`,
                      `${h * 0.7}%`,
                      `${Math.min(100, h * 1.05)}%`,
                      `${h * 0.6}%`,
                    ],
                  }
            }
            transition={{
              duration: 3,
              delay: i * 0.12,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>
    </DemoFrame>
  );
}

/* ------------------------------------------------------------------ */
/*  AI IMPACT, Before vs After comparison with animated bars          */
/* ------------------------------------------------------------------ */
function AIImpactCompare() {
  const metrics = [
    { label: "Time to first interview",   before: "14 days",  beforePct: 100, after: "3 days",   afterPct: 22 },
    { label: "Resumes screened per hire", before: "80",       beforePct: 21,  after: "380",      afterPct: 100 },
    { label: "Cost per hire",             before: "$4,200",   beforePct: 100, after: "$1,650",   afterPct: 39 },
    { label: "Recruiter hours / week",    before: "32 hrs",   beforePct: 100, after: "11 hrs",   afterPct: 34 },
  ];

  return (
    <div
      className="mt-12 border"
      style={{ borderColor: C.hairline, background: "#FFFFFF" }}
    >
      {/* Header strip */}
      <div
        className="flex items-center justify-between px-5 md:px-6 py-3 border-b flex-wrap gap-2"
        style={{ borderColor: C.hairline }}
      >
        <div className="flex items-center gap-2">
          <span
            className="w-2 h-2"
            style={{ background: C.forest }}
            aria-hidden="true"
          />
          <TechLabel style={{ color: C.forest }}>
            Impact · before / after
          </TechLabel>
          <span
            className="hidden sm:inline-block w-px h-3 mx-1"
            style={{ background: C.hairline }}
          />
          <TechLabel style={{ color: `${C.grid}aa` }}>
            avg of 06 customer cohorts
          </TechLabel>
        </div>
        <TechLabel style={{ color: `${C.grid}99` }}>
          benchmark · validated
        </TechLabel>
      </div>

      {/* Side-by-side panels */}
      <div
        className="grid md:grid-cols-2 gap-px"
        style={{ background: C.hairline }}
      >
        {/* WITHOUT */}
        <div
          className="p-6 md:p-8"
          style={{ background: "#FFFFFF" }}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <span
                className="w-2 h-2"
                style={{ background: C.coral }}
                aria-hidden="true"
              />
              <TechLabel style={{ color: C.coral }}>
                01 · Without WebIdeal AI
              </TechLabel>
            </div>
            <TechLabel style={{ color: `${C.grid}88` }}>
              baseline
            </TechLabel>
          </div>
          <div className="space-y-5">
            {metrics.map((m, i) => (
              <ImpactBar
                key={m.label}
                label={m.label}
                value={m.before}
                pct={m.beforePct}
                color={C.coral}
                delay={i * 0.08}
              />
            ))}
          </div>
        </div>

        {/* WITH */}
        <div
          className="relative p-6 md:p-8 overflow-hidden"
          style={{ background: C.forest, color: C.paper }}
        >
          {/* corner markers echo hero */}
          <span
            aria-hidden="true"
            className="absolute top-2 left-2 w-2.5 h-2.5 border-l-2 border-t-2"
            style={{ borderColor: C.mint }}
          />
          <span
            aria-hidden="true"
            className="absolute top-2 right-2 w-2.5 h-2.5 border-r-2 border-t-2"
            style={{ borderColor: C.mint }}
          />
          <span
            aria-hidden="true"
            className="absolute bottom-2 left-2 w-2.5 h-2.5 border-l-2 border-b-2"
            style={{ borderColor: C.mint }}
          />
          <span
            aria-hidden="true"
            className="absolute bottom-2 right-2 w-2.5 h-2.5 border-r-2 border-b-2"
            style={{ borderColor: C.mint }}
          />

          <div className="relative flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <span
                className="w-2 h-2"
                style={{ background: C.mint }}
                aria-hidden="true"
              />
              <TechLabel style={{ color: C.mint }}>
                02 · With WebIdeal AI
              </TechLabel>
            </div>
            <TechLabel style={{ color: "rgba(247,247,245,0.55)" }}>
              uplift
            </TechLabel>
          </div>
          <div className="relative space-y-5">
            {metrics.map((m, i) => (
              <ImpactBar
                key={m.label}
                label={m.label}
                value={m.after}
                pct={m.afterPct}
                color={C.mint}
                delay={i * 0.08 + 0.15}
                dark
              />
            ))}
          </div>
        </div>
      </div>

      {/* Footer summary strip */}
      <div
        className="border-t px-5 md:px-6 py-3 flex items-center justify-between flex-wrap gap-3"
        style={{ borderColor: C.hairline }}
      >
        <TechLabel style={{ color: `${C.grid}aa` }}>
          fig 04 · platform impact
        </TechLabel>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <span
              className="w-1.5 h-1.5"
              style={{ background: C.mint }}
            />
            <TechLabel style={{ color: C.forest }}>
              4.6× faster pipeline
            </TechLabel>
          </div>
          <div className="flex items-center gap-1.5">
            <span
              className="w-1.5 h-1.5"
              style={{ background: C.gold }}
            />
            <TechLabel style={{ color: C.forest }}>
              60% lower cost
            </TechLabel>
          </div>
        </div>
      </div>
    </div>
  );
}

function ImpactBar({ label, value, pct, color, delay = 0, dark = false }) {
  const reduce = useReducedMotion();
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span
          className="text-xs font-medium"
          style={{ color: dark ? "rgba(247,247,245,0.75)" : C.grid }}
        >
          {label}
        </span>
        <span
          className="text-base font-extrabold tabular-nums"
          style={{ color: dark ? C.paper : C.forest }}
        >
          {value}
        </span>
      </div>
      <div
        className="h-2 relative overflow-hidden"
        style={{
          background: dark ? "rgba(158,255,191,0.15)" : C.hairline,
        }}
      >
        <motion.div
          className="absolute inset-y-0 left-0"
          style={{ background: color }}
          initial={{ width: reduce ? `${pct}%` : "0%" }}
          whileInView={{ width: `${pct}%` }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{
            duration: reduce ? 0 : 1.1,
            ease: [0.25, 0.1, 0.25, 1],
            delay,
          }}
        />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  PAGE                                                               */
/* ------------------------------------------------------------------ */

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 50]);

  const handleBookDemo = () => {
    window.location.href = createPageUrl("Contact");
  };
  const handleGetStarted = () => {
    window.location.href = "/jobs/auth";
  };

  return (
    <div
      className="relative"
      style={{
        fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
        background: C.paper,
        color: C.forest,
      }}
    >
      <MosaicBackground />

      {/* =====================  HERO  ===================== */}
      <section
        ref={heroRef}
        className="relative pt-16 pb-24 md:pt-24 md:pb-32 border-b"
        style={{ borderColor: C.hairline }}
      >
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 xl:gap-20 items-center">
            {/* Copy */}
            <motion.div
              className="lg:col-span-7"
              style={{ y: heroY }}
              initial="hidden"
              animate="show"
              variants={fadeUp}
            >
              {/* Indexed pre-headline with vertical hairline */}
              <motion.div variants={fadeUp} custom={0} className="flex items-stretch gap-4 mb-7">
                <span className="w-px self-stretch" style={{ background: C.forest }} />
                <div className="flex flex-col gap-1.5">
                  <TechLabel style={{ color: `${C.grid}99` }}>
                    AI · ATS · CRM · HRMS · JOBS
                  </TechLabel>
                </div>
              </motion.div>

              <motion.h1
                variants={fadeUp}
                custom={1}
                className="text-5xl sm:text-6xl lg:text-[5.25rem] xl:text-[6.25rem] 2xl:text-[7rem] font-extrabold leading-[0.92] tracking-tight"
                style={{ color: C.forest }}
              >
                Hire smarter.
                <br />
                <span className="inline-block">Build faster.</span>
                <br />
                <span style={{ color: `${C.forest}80` }}>Grow humanly.</span>
              </motion.h1>

              <motion.p
                variants={fadeUp}
                custom={2}
                className="mt-8 text-lg md:text-xl leading-relaxed max-w-3xl"
                style={{ color: `${C.grid}` }}
              >
                One unified platform  <strong style={{ color: C.forest }}>ATS, CRM, HRMS and Job Portal</strong>{" "}
                orchestrated by AI agents that screen, match and move your pipeline forward 24/7.
              </motion.p>

              <motion.div variants={fadeUp} custom={3} className="mt-10 flex flex-wrap items-center gap-3">
                <Button
                  onClick={handleBookDemo}
                  className="cursor-pointer h-12 px-7 text-sm font-semibold rounded-none transition-colors duration-150 group"
                  style={{ background: C.forest, color: C.paper }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#244f3a")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = C.forest)}
                >
                  <Rocket className="w-4 h-4 mr-2" />
                  Book a demo
                </Button>

                <Link to={createPageUrl("Products")}>
                  <Button
                    variant="ghost"
                    className="cursor-pointer h-12 px-6 text-sm font-semibold rounded-none border bg-transparent transition-colors duration-150 group"
                    style={{ borderColor: C.forest, color: C.forest }}
                  >
                    Explore the platform
                    <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
                  </Button>
                </Link>
              </motion.div>

              {/* Indexed trust strip */}
              <motion.div variants={fadeUp} custom={4} className="mt-12 grid grid-cols-1 sm:grid-cols-3 border-t border-b" style={{ borderColor: C.hairline }}>
                {[
                  ["SOC 2 + GDPR", "Compliance ready"],
                  ["5 countries", "Active deployment"],
                  ["G2 4.9 / 5", "Customer rating"],
                ].map(([idx, t, s], i, arr) => (
                  <div
                    key={idx}
                    className={`py-5 px-4 ${i < arr.length - 1 ? "sm:border-r" : ""} border-b sm:border-b-0`}
                    style={{ borderColor: C.hairline }}
                  >
                    <TechLabel style={{ color: `${C.grid}99` }}>{idx}</TechLabel>
                    <p className="mt-1.5 font-bold text-base" style={{ color: C.forest }}>{t}</p>
                    <p className="text-xs mt-0.5" style={{ color: `${C.grid}` }}>{s}</p>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Visual */}
            <motion.div
              className="lg:col-span-5"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <BlueprintTopology />
            </motion.div>
          </div>
        </div>
      </section>

      {/* =====================  STATS MARQUEE  ===================== */}
      <section
        className="border-b overflow-hidden"
        style={{ borderColor: C.hairline, background: C.paper }}
      >
        <div className="relative">
          <motion.div
            className="flex gap-12 py-5 whitespace-nowrap"
            animate={{ x: ["0%", "-50%"] }}
            transition={{ duration: 40, ease: "linear", repeat: Infinity }}
          >
            {[...stats, ...stats, ...stats].map((s, i) => (
              <div key={i} className="flex items-center gap-4 shrink-0">
                <span
                  className="text-3xl md:text-4xl font-extrabold tracking-tight"
                  style={{ color: C.forest, fontVariantNumeric: "tabular-nums" }}
                >
                  {s.value}
                </span>
                <TechLabel style={{ color: `${C.grid}` }}>
                  {s.label}
                </TechLabel>
                <span className="w-1.5 h-1.5" style={{ background: C.forest, opacity: 0.4 }} />
              </div>
            ))}
          </motion.div>
          <div
            className="pointer-events-none absolute inset-y-0 left-0 w-24"
            style={{ background: `linear-gradient(to right, ${C.paper}, transparent)` }}
          />
          <div
            className="pointer-events-none absolute inset-y-0 right-0 w-24"
            style={{ background: `linear-gradient(to left, ${C.paper}, transparent)` }}
          />
        </div>
      </section>

      {/* =====================  PROBLEM → SOLUTION  ===================== */}
      <section className="py-24 border-b" style={{ borderColor: C.hairline }}>
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
          <div className="mb-12 flex items-center justify-between flex-wrap gap-4">
            <StatusBadge>Problem / Solution</StatusBadge>

          </div>

          <div className="grid lg:grid-cols-2 gap-px" style={{ background: C.hairline }}>
            {/* Problem card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5 }}
              className="relative p-8 md:p-10"
              style={{ background: C.paper, borderLeft: `4px solid ${C.coral}` }}
            >
              <TechLabel style={{ color: C.coral }}>The Problem</TechLabel>
              <h3
                className="mt-3 text-3xl md:text-4xl font-bold leading-[1.05] tracking-tight"
                style={{ color: C.forest }}
              >
                Hiring stacks are fragmented and slow.
              </h3>
              <ul className="mt-8 space-y-px" style={{ background: C.hairline }}>
                {problems.map((p, i) => (
                  <li
                    key={p.title}
                    className="flex items-start gap-4 p-4"
                    style={{ background: C.paper }}
                  >
                    <span
                      className="shrink-0 w-9 h-9 flex items-center justify-center border"
                      style={{ borderColor: `${C.coral}66`, color: C.coral }}
                    >
                      <p.icon className="w-4 h-4" strokeWidth={1.6} />
                    </span>
                    <div>
                      <p className="font-bold" style={{ color: C.forest }}>{p.title}</p>
                      <p className="text-sm mt-0.5" style={{ color: `${C.grid}` }}>{p.description}</p>
                    </div>
                    <TechLabel
                      className="ml-auto pt-2 shrink-0"
                      style={{ color: `${C.grid}99` }}
                    >
                      0{i + 1}
                    </TechLabel>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Solution card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: 0.08 }}
              className="relative p-8 md:p-10"
              style={{ background: C.forest, color: C.paper }}
            >
              <CornerMarkers color={C.mint} />
              <TechLabel style={{ color: C.mint }}>The WebIdeal Way</TechLabel>
              <h3 className="mt-3 text-3xl md:text-4xl font-bold leading-[1.05] tracking-tight">
                One AI brain. Every step of the hire.
              </h3>
              <p className="mt-5 leading-relaxed" style={{ color: "rgba(247,247,245,0.78)" }}>
                Replace four disconnected tools with one platform wired
                end-to-end with AI agents that screen, score, schedule and onboard.
              </p>
              <div className="mt-8 grid grid-cols-2 gap-px" style={{ background: "rgba(158,255,191,0.25)" }}>
                {[
                  "Unified data model",
                  "Predictive fit scoring",
                  "Automated workflows",
                  "Real-time analytics",
                ].map((t, i) => (
                  <div key={t} className="p-4 flex items-start gap-3" style={{ background: C.forest }}>
                    <TechLabel style={{ color: C.mint }}>0{i + 1}</TechLabel>
                    <span className="text-sm font-medium leading-snug">{t}</span>
                  </div>
                ))}
              </div>
              <Link
                to={createPageUrl("Products")}
                className="mt-8 inline-flex items-center gap-2 font-semibold group"
                style={{ color: C.mint }}
              >
                See how it works
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* =====================  THE SUITE, Forest Bento  ===================== */}
      <section className="py-24 border-b" style={{ borderColor: C.hairline }}>
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
          <div className="mb-12 flex items-end justify-between flex-wrap gap-4">
            <div className="max-w-2xl">
              <StatusBadge>The Suite</StatusBadge>
              <h2
                className="mt-4 text-4xl md:text-5xl xl:text-6xl font-bold leading-[1.05] tracking-tight"
                style={{ color: C.forest }}
              >
                Four products.{" "}
                <span style={{ color: `${C.forest}80` }}>
                  One ecosystem.
                </span>
              </h2>
              <p className="mt-4 text-lg" style={{ color: C.grid }}>
                Every tool you need, sharing the same data, the same AI brain
                and the same beautiful interface.
              </p>
            </div>
            <TechLabel style={{ color: `${C.grid}99` }}>
              04 modules · unified core
            </TechLabel>
          </div>
                    
          <div
            className="grid md:grid-cols-2 gap-px auto-rows-[minmax(260px,auto)]"
            style={{ background: C.hairline }}
          >
            {products.map((p, i) => {
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.55, delay: i * 0.08 }}
                >
                  <Link
                    to={p.href}
                    className="group relative h-full block p-7 md:p-8 overflow-hidden transition-colors duration-200 hover:bg-white"
                    style={{
                      background: C.paper,
                      borderLeft: `4px solid ${p.accent}`,
                    }}
                  >
                    {/* Subtle accent glow */}
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 opacity-[0.10] blur-3xl transition-opacity duration-500 group-hover:opacity-[0.22]"
                      style={{ background: p.accent }}
                    />
      
                    {/* Top row: icon block + numbered tag + arrow */}
                    <div className="relative flex items-start justify-between mb-6">
                      <div
                        className="w-12 h-12 flex items-center justify-center transition-transform duration-300 group-hover:-translate-y-0.5"
                        style={{ background: C.forest, color: C.paper }}
                      >
                        <p.icon className="w-5 h-5" strokeWidth={1.6} />
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <TechLabel style={{ color: p.accent }}>
                          P-0{i + 1}
                        </TechLabel>
                        <span
                          className="cursor-pointer w-8 h-8 flex items-center justify-center border transition-colors duration-200 group-hover:border-[#1A3C2B] group-hover:bg-[#1A3C2B]"
                          style={{
                            borderColor: C.hairline,
                            color: C.grid,
                          }}
                        >
                          <ArrowUpRight
                            className="w-4 h-4 transition-colors duration-200 group-hover:text-[#F7F7F5]"
                          />
                        </span>
                      </div>
                    </div>
                        
                    {/* Body */}
                    <div className="relative">
                      <TechLabel style={{ color: `${C.grid}99` }}>
                        {p.subtitle}
                      </TechLabel>
                      <h3
                        className="mt-2 text-2xl md:text-[1.7rem] font-bold leading-tight tracking-tight transition-colors duration-200"
                        style={{ color: C.forest }}
                      >
                        {p.title}
                      </h3>
                      <p
                        className="mt-3 text-sm leading-relaxed max-w-md"
                        style={{ color: C.grid }}
                      >
                        {p.description}
                      </p>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* =====================  AI CAPABILITIES, Live Orchestrator + Animated Agents  ===================== */}
      <section className="py-24 border-b" style={{ borderColor: C.hairline }}>
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
          <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
            <div className="max-w-2xl">
              <StatusBadge>AI Agents</StatusBadge>
              <h2
                className="mt-4 text-4xl md:text-5xl xl:text-6xl font-bold leading-[1.05] tracking-tight"
                style={{ color: C.forest }}
              >
                Built for the AI-native era.
              </h2>
              <p className="mt-4 text-lg" style={{ color: C.grid }}>
                Six specialised agents do the heavy lifting so your team focuses
                on people, not paperwork.
              </p>
            </div>
            <TechLabel style={{ color: `${C.grid}99` }}>
              06 agents · 24 / 7 uptime
            </TechLabel>
          </div>

          {/* Animated AI Orchestrator, packet flowing through all 6 agents */}
          <AIOrchestrator agents={aiCapabilities} />

          {/* Cards with embedded mini animations */}
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px"
            style={{ background: C.hairline }}
          >
            {aiCapabilities.map((c, i) => (
              <AgentCard key={c.title} agent={c} index={i} />
            ))}
          </div>

          {/* Before/after impact comparison */}
          <AIImpactCompare />

          <div className="mt-12 text-center">
            <Link to={createPageUrl("AIAutomation")}>
              <Button
                variant="ghost"
                className="cursor-pointer h-12 px-7 text-sm font-semibold rounded-none border bg-transparent transition-colors duration-150"
                style={{ borderColor: C.forest, color: C.forest }}
              >
                <Play className="w-4 h-4 mr-2" />
                See AI in action
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* =====================  WHY US, Forest panel bento  ===================== */}
      <section
        className="py-24 border-b"
        style={{ background: C.forest, color: C.paper, borderColor: C.hairline }}
      >
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
          <div className="mb-14 flex items-end justify-between flex-wrap gap-4">
            <div className="max-w-2xl">
              <StatusBadge color={C.mint}>Why WebIdeal</StatusBadge>
              <h2 className="mt-4 text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight">
                Built different. Built better.
              </h2>
              <p className="mt-4 text-lg" style={{ color: "rgba(247,247,245,0.75)" }}>
                Nine reasons modern teams switch to WebIdeal and stay.
              </p>
            </div>
            <TechLabel style={{ color: "rgba(158,255,191,0.7)" }}>
              09 reasons · validated
            </TechLabel>
          </div>

          <div
            className="grid md:grid-cols-3 gap-px"
            style={{ background: "rgba(158,255,191,0.18)" }}
          >
            {whyChooseUs.map((w, i) => {
              const accents = [C.mint, C.coral, C.gold];
              const acc = accents[i % accents.length];
              return (
                <motion.div
                  key={w.title}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                  className="relative p-7 group cursor-pointer transition-colors duration-150"
                  style={{ background: C.forest }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#22503a")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = C.forest)}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div
                      className="w-10 h-10 flex items-center justify-center border"
                      style={{ borderColor: acc, color: acc }}
                    >
                      <w.icon className="w-4 h-4" strokeWidth={1.6} />
                    </div>
                    <TechLabel style={{ color: acc }}>0{i + 1}</TechLabel>
                  </div>
                  <h3 className="text-lg font-bold leading-tight">{w.title}</h3>
                  <p
                    className="mt-2 text-sm leading-relaxed"
                    style={{ color: "rgba(247,247,245,0.75)" }}
                  >
                    {w.description}
                  </p>
                  <ArrowUpRight
                    className="absolute top-6 right-6 w-4 h-4 transition-all duration-150 opacity-0 group-hover:opacity-100 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                    style={{ color: acc }}
                  />
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* =====================  GLOBAL  ===================== */}
      <section className="py-24 border-b" style={{ borderColor: C.hairline }}>
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.5 }}
            className="relative border p-10 md:p-14 text-center"
            style={{ borderColor: C.hairline, background: C.paper }}
          >
            <CornerMarkers color={C.forest} />
            <div className="flex justify-center mb-5">
              <StatusBadge>Global</StatusBadge>
            </div>
            <h2
              className="text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight"
              style={{ color: C.forest }}
            >
              Global hiring. Local expertise.
            </h2>
            <p className="mt-4 text-lg max-w-2xl mx-auto" style={{ color: C.grid }}>
              Headquartered in India with strategic presence across major
              markets worldwide.
            </p>
            <div
              className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px max-w-4xl mx-auto"
              style={{ background: C.hairline }}
            >
              {countries.map((c) => (
                <div
                  key={c.code}
                  className="flex flex-col items-center gap-2 py-6 px-3 transition-colors duration-150 cursor-default hover:bg-white"
                  style={{ background: C.paper }}
                >
                  <span
                    className="w-10 h-10 flex items-center justify-center text-sm font-bold border"
                    style={{ borderColor: C.forest, color: C.forest }}
                  >
                    {c.code}
                  </span>
                  <span className="font-medium text-sm" style={{ color: C.forest }}>
                    {c.country}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* =====================  TECHNICAL FORM CTA  ===================== */}
      <section className="py-24">
        <div className="max-w-[640px] mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5 }}
            className="relative border p-8 md:p-10"
            style={{ borderColor: C.hairline, background: C.paper }}
          >
            <CornerMarkers color={C.forest} />

            <div className="flex items-center gap-3 mb-6">
              <StatusBadge>Get started</StatusBadge>
            </div>

            <h2
              className="text-3xl md:text-4xl font-bold leading-[1.05] tracking-tight"
              style={{ color: C.forest }}
            >
              Ready to make hiring smarter?
            </h2>
            <p className="mt-4 text-base" style={{ color: C.grid }}>
              Join forward-thinking teams using WebIdeal AI to ship offers
              faster, with better candidates and happier hiring managers.
            </p>

            {/* Mini "form" */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleBookDemo();
              }}
              className="mt-8 space-y-5"
            >
              <div>
                <label htmlFor="cta-email" className="block mb-2">
                  <TechLabel style={{ color: C.grid }}>
                    Work email
                  </TechLabel>
                </label>
                <input
                  id="cta-email"
                  type="email"
                  placeholder="you@company.com"
                  className="w-full h-11 px-3 text-sm border focus:outline-none transition-colors duration-150 bg-white"
                  style={{
                    borderColor: C.hairline,
                    color: C.forest,
                    fontFamily: "inherit",
                  }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = C.forest)}
                  onBlur={(e) => (e.currentTarget.style.borderColor = C.hairline)}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <Button
                  type="submit"
                  className="cursor-pointer h-12 text-sm font-semibold rounded-none transition-colors duration-150"
                  style={{ background: C.forest, color: C.paper }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#244f3a")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = C.forest)}
                >
                  <Rocket className="w-4 h-4 mr-2" />
                  Request demo
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleGetStarted}
                  className="cursor-pointer h-12 text-sm font-semibold rounded-none border bg-transparent transition-colors duration-150"
                  style={{ borderColor: C.forest, color: C.forest }}
                >
                  Start free trial
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </form>

            <div
              className="mt-8 pt-5 border-t flex items-center justify-between"
              style={{ borderColor: C.hairline }}
            >
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-3.5 h-3.5" style={{ color: C.forest }} />
                <TechLabel style={{ color: C.grid }}>
                  No credit card · Setup in minutes
                </TechLabel>
              </div>
              <TechLabel style={{ color: `${C.grid}99` }}>
                <Sparkles className="w-3 h-3 inline mr-1" />
                Powered by AI
              </TechLabel>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
